import dayjs from 'dayjs';
import {
    Event,
    Color,
    RecurringType,
    TServerSimpleCalendarEvent,
    EScheduleStatusValue,
    TServerCalendarAppointmentEventDetail,
    EScheduledBy,
} from 'types/calendar';
import { DEFAULT_CALENDAR_COLOR, SUPPORT_TIME_ZONE } from 'constants/common';
import { getBrowserTimezoneOffset } from './common';
import { EOnBoardingStatus, KlarityService, Prospect } from 'types/common';

const getMonthDaysMatrix = (_month: number = dayjs().month()): dayjs.Dayjs[][] => {
    const month = Math.floor(_month);
    const year = dayjs().year();
    const firstDayOfTheMonth = dayjs(new Date(year, month, 1)).day();
    let currentMonthCount = 0 - firstDayOfTheMonth;
    const daysMatrix = new Array(5).fill([]).map(() => {
        return new Array(7).fill(null).map(() => {
            currentMonthCount++;
            return dayjs(new Date(year, month, currentMonthCount));
        });
    });
    return daysMatrix;
};

const getWeekDaysArray = (date: number = dayjs().day(), monthIndex: number = dayjs().month()): dayjs.Dayjs[] => {
    const year = dayjs().year();
    const current = dayjs(new Date(year, monthIndex, date));
    const start = current.startOf('week');
    const daysArray = [start];
    for (let i = 1; i < 7; i++) {
        daysArray.push(start.add(i, 'day'));
    }

    return daysArray;
};

const getTimeStr = (date?: dayjs.Dayjs, format?: string, timezone?: string): string => {
    if (!date || !date.isValid()) {
        return '';
    }
    const dateStr = date.format(format || 'YYYY-MM-DD HH:mm:ss');
    return dateStr;
};

const getTimeStrForGoogleCalandar = (date: dayjs.Dayjs, timezone?: string) => {
    let dateStr = date.format('YYYY-MM-DDTHH:mm:ss');
    let offset = getBrowserTimezoneOffset();
    if (timezone) {
        const target = SUPPORT_TIME_ZONE.find((item) => item.dictKey.toLowerCase() === timezone.toLowerCase());
        if (target) {
            offset = target.offset;
        }
    }
    dateStr = `${dateStr}${offset}:00`;
    return dateStr;
};

const getDateKey = (date?: dayjs.Dayjs): string => {
    if (!date) {
        return '';
    }
    return date.format('YYYY-MM-DD'); //fix format
};

const getEventColor = (event: Event, colors: Color[]): string => {
    // const target = colors?.find((color) => {
    //     return color.dictKey === event.colorId;
    // });
    return event?.color || DEFAULT_CALENDAR_COLOR;
};

const isEventTimeOverlap = (event1: Event, event2: Event): boolean => {
    const event1Start = new Date(event1.start.dateTime).getTime();
    const event1End = new Date(event1.end.dateTime).getTime();
    const event2Start = new Date(event2.start.dateTime).getTime();
    const event2End = new Date(event2.end.dateTime).getTime();
    if (event1End <= event2Start) {
        return false;
    }
    if (event2End <= event1Start) {
        return false;
    }
    return true;
};

const getRecurrence = (time: dayjs.Dayjs, type: RecurringType): string | undefined => {
    if (type === RecurringType.DAILY) {
        return 'RRULE:FREQ=DAILY';
    }
    if (type === RecurringType.NOT_REPEAT) {
        return undefined;
    }
    if (type === RecurringType.WEEKLY) {
        const weekDay = time.format('dd');
        return `RRULE:FREQ=WEEKLY;BYDAY=${weekDay.toLocaleUpperCase()}`;
    }
    if (type === RecurringType.MONTHLY_ON_FIRST_WEEKDAY) {
        const weekDay = time.format('dd');
        return `RRULE:FREQ=MONTHLY;BYDAY=1${weekDay.toLocaleUpperCase()}`;
    }
    if (type === RecurringType.MONTHLY_ON_DATE) {
        return 'RRULE:FREQ=MONTHLY';
    }
    if (type === RecurringType.WEEKDAY) {
        const weekDay = time.format('dd');
        return `RRULE:FREQ=WEEKLY;BYDAY=${weekDay.toLocaleUpperCase()}`;
    }
    return undefined;
};

const getRecurrenceTypeFromStr = (recurrence?: string): RecurringType => {
    if (!recurrence) {
        return RecurringType.NOT_REPEAT;
    }
    if (recurrence.includes('RRULE:FREQ=DAILY')) {
        return RecurringType.DAILY;
    }
    if (recurrence.includes('RRULE:FREQ=MONTHLY;BYDAY=1')) {
        return RecurringType.MONTHLY_ON_FIRST_WEEKDAY;
    }
    if (recurrence.includes('RRULE:FREQ=MONTHLY')) {
        return RecurringType.MONTHLY_ON_DATE;
    }
    if (recurrence.includes('RRULE:FREQ=WEEKLY;BYDAY=')) {
        return RecurringType.WEEKDAY;
    }
    if (recurrence.includes('RRULE:FREQ=WEEKLY')) {
        return RecurringType.WEEKLY;
    }

    return RecurringType.NOT_REPEAT;
};

const isCancelledEvent = (
    event: TServerSimpleCalendarEvent | Event | TServerCalendarAppointmentEventDetail,
): boolean => {
    return !!(
        event?.appointmentStatus &&
        [
            EScheduleStatusValue.PATIENT_CANCELED,
            EScheduleStatusValue.PROVIDER_CANCELED,
            EScheduleStatusValue.PATIENT_NO_SHOW,
            EScheduleStatusValue.PATIENT_LATE_CANCELED,
        ].includes(event.appointmentStatus)
    );
};

const getEventTitleDisplay = (event?: Event): string => {
    if (!event || !event.summary) return '';

    // Normalize common prefixes so calendar cards read consistently.
    let summary = `${event.summary}`.trim();
    summary = summary.replace(/^\[Klarity\]\s*/i, '');
    summary = summary.replace(/^\[Sample\]\s*/i, '');

    // Expected calendar format:
    // "{First name Last name} - [WA] Specialty: Visit type"
    // We display:
    // "{First name Last name} - Specialty: Visit type"
    const dashIdx = summary.indexOf(' - ');
    if (dashIdx > 0) {
        const name = summary.slice(0, dashIdx).trim();
        let rest = summary.slice(dashIdx + 3).trim();
        rest = rest.replace(/^\[[^\]]+\]\s*/g, ''); // drop leading state / tags like "[WA]"

        const colonIdx = rest.indexOf(':');
        if (colonIdx > 0) {
            const specialty = rest.slice(0, colonIdx).trim();
            const visitType = rest.slice(colonIdx + 1).trim();
            if (name && specialty && visitType) {
                return `${name} - ${specialty}: ${visitType}`;
            }
        }
    }

    // Preserve legacy behavior for bracketed titles.
    if (summary.startsWith('[')) return summary;
    if (event.scheduledBy === EScheduledBy.KLARITY_BOOKED) return `[Klarity] ${summary}`;
    return summary;
};

const getIndexOfWeekday = (date?: dayjs.Dayjs): number => {
    if (!date) {
        return 0;
    }
    const startOfMonth = date.startOf('month');
    const endOfMonth = date.endOf('month');

    let count = 0;
    for (let day = startOfMonth; day.isBefore(endOfMonth); day = day.add(1, 'day')) {
        if (day.day() === date.day()) {
            // 检查是否为星期三（0是周日，1是周一，...，3是周三）
            count++;
            if (day.isSame(date, 'day')) {
                break;
            }
        }
    }
    return count;
};
const hexToLightBg = (hex: string, mix = 0.85): string => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    const lr = Math.round(r + (255 - r) * mix);
    const lg = Math.round(g + (255 - g) * mix);
    const lb = Math.round(b + (255 - b) * mix);
    return `rgb(${lr}, ${lg}, ${lb})`;
};

//color, border color, background color
const getEventColorSetting = (
    evt: Event,
    allClients?: Prospect[],
    providerColorMap?: Record<number, string>,
): {
    color: string;
    borderColor: string;
    backgroundColor: string;
    borderStyle: string; //'solid' | 'dashed' | 'dotted',
} => {
    if (providerColorMap && evt.providerId && providerColorMap[evt.providerId]) {
        const pc = providerColorMap[evt.providerId];
        return {
            color: pc,
            borderColor: pc,
            backgroundColor: hexToLightBg(pc),
            borderStyle: 'solid',
        };
    }
    let ret = {
        color: '#0369A1',
        borderColor: '#1647A3',
        backgroundColor: '#F1F9FF',
        borderStyle: 'dashed',
    };
    const eventColor = evt?.color;
    let targetClient: Prospect | undefined;
    if (evt.clientId && allClients) {
        targetClient = allClients.find((item) => {
            return `${item.id}` === `${evt.clientId}`;
        });
    }
    const isProtect = !!(targetClient && targetClient?.onBoardingStatus === EOnBoardingStatus.COMPLETE);
    if (isProtect) {
        ret = {
            color: '#FFFFFF',
            borderColor: '#1647A3',
            backgroundColor: '#1D86DB',
            borderStyle: 'solid',
        };
    }
    //const isProtect = evt?.summary?.includes("Xiaocong");

    if (eventColor === '#1D86DB') {
        if (isProtect) {
            ret = {
                color: '#FFFFFF',
                borderColor: '#1647A3',
                backgroundColor: '#1D86DB',
                borderStyle: 'solid',
            };
        } else {
            ret = {
                color: '#0369A1',
                borderColor: '#1647A3',
                backgroundColor: '#D3EBFF',
                borderStyle: 'dashed',
            };
        }
    }
    if (eventColor === '#009572') {
        if (isProtect) {
            ret = {
                color: '#FFFFFF',
                borderColor: '#0F766E',
                backgroundColor: '#009572',
                borderStyle: 'solid',
            };
        } else {
            ret = {
                color: '#0F766E',
                borderColor: '#0F766E',
                backgroundColor: '#F0FDFA',
                borderStyle: 'dashed',
            };
        }
    }

    if (eventColor === '#E3DDFF') {
        if (isProtect) {
            ret = {
                color: '#4C1D95',
                borderColor: '#6D28D9',
                backgroundColor: '#C4B5FD',
                borderStyle: 'solid',
            };
        } else {
            ret = {
                color: '#6D28D9',
                borderColor: '#6D28D9',
                backgroundColor: '#F5F3FF',
                borderStyle: 'dashed',
            };
        }
    }

    if (eventColor === '#F8F88F') {
        if (isProtect) {
            ret = {
                color: '#B45309',
                borderColor: '#B45309',
                backgroundColor: '#F4EA56',
                borderStyle: 'solid',
            };
        } else {
            ret = {
                color: '#B45309',
                borderColor: '#B45309',
                backgroundColor: '#FFFBBF',
                borderStyle: 'dashed',
            };
        }
    }

    if (eventColor === '#333D96') {
        if (isProtect) {
            ret = {
                color: '#F0F9FF',
                borderColor: '#333D96',
                backgroundColor: '#333D96',
                borderStyle: 'solid',
            };
        } else {
            ret = {
                color: '#333D96',
                borderColor: '#333D96',
                backgroundColor: '#CFDFEE',
                borderStyle: 'dashed',
            };
        }
    }

    if (eventColor === '#FFE8B4') {
        if (isProtect) {
            ret = {
                color: '#92400E',
                borderColor: '#D97706',
                backgroundColor: '#FBBF24',
                borderStyle: 'solid',
            };
        } else {
            ret = {
                color: '#D97706',
                borderColor: '#D97706',
                backgroundColor: '#FCFFEC',
                borderStyle: 'dashed',
            };
        }
    }

    if (eventColor === '#FCA5A5') {
        if (isProtect) {
            ret = {
                color: '#7F1D1D',
                borderColor: '#DC2626',
                backgroundColor: '#FCA5A5',
                borderStyle: 'solid',
            };
        } else {
            ret = {
                color: '#DC2626',
                borderColor: '#DC2626',
                backgroundColor: '#FFF6FB',
                borderStyle: 'dashed',
            };
        }
    }

    if (eventColor === '#B9FFFF') {
        if (isProtect) {
            ret = {
                color: '#164E63',
                borderColor: '#164E63',
                backgroundColor: '#6AFFFF',
                borderStyle: 'solid',
            };
        } else {
            ret = {
                color: '#164E63',
                borderColor: '#164E63',
                backgroundColor: '#CAFFFF',
                borderStyle: 'dashed',
            };
        }
    }

    return ret;
};

export {
    getIndexOfWeekday,
    getMonthDaysMatrix,
    getWeekDaysArray,
    getTimeStr,
    getTimeStrForGoogleCalandar,
    getDateKey,
    getEventColor,
    isEventTimeOverlap,
    getRecurrence,
    getRecurrenceTypeFromStr,
    isCancelledEvent,
    getEventTitleDisplay,
    getEventColorSetting,
};
